import { motion } from "framer-motion";
import {
  Sparkles,
  ShieldCheck,
  Beaker,
  Factory,
  Leaf,
  Globe2,
  ArrowUpRight,
} from "lucide-react";
import bgImage from '../assets/bg.png';

const features = [
  {
    title: "R&D Driven Innovation",
    desc: "Advanced formulations crafted with research-first product development.",
    icon: Beaker,
  },
  {
    title: "Global Quality Standards",
    desc: "Strict protocols and testing ensure export-grade safety and compliance.",
    icon: ShieldCheck,
  },
  {
    title: "State-of-the-art Facilities",
    desc: "Modern manufacturing built for precision, consistency, and scalability.",
    icon: Factory,
  },
  {
    title: "Sustainable Practices",
    desc: "Responsible sourcing and processes designed for long-term impact.",
    icon: Leaf,
  },
];

const reveal = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.12 },
  },
};

export default function DietaryHealthcareSection() {
  return (
    <section className="relative py-20 md:py-28 overflow-hidden bg-[#070A0F]">
        {/* ===== Premium Dark Background (Image + overlays) ===== */}
<div className="pointer-events-none absolute inset-0">
  {/* Background image */}
  <div
    className="absolute inset-0 bg-center bg-cover"
    style={{ backgroundImage: `url(${bgImage})` }}
  />

  {/* Dark readability overlay */}
  <div className="absolute inset-0 bg-[#070A0F]/70" />

  {/* Brand glow mesh */}
  <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_10%,rgba(244,124,32,0.18),transparent_60%),radial-gradient(900px_circle_at_85%_20%,rgba(232,62,111,0.16),transparent_60%),radial-gradient(900px_circle_at_60%_90%,rgba(201,162,39,0.12),transparent_55%)]" />

  {/* Soft vignette depth */}
  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_35%,rgba(0,0,0,0.55)_100%)]" />
</div>
      {/* Ambient glows */}
      <div className="pointer-events-none absolute -top-28 -left-28 h-80 w-80 rounded-full blur-3xl opacity-10 bg-[#F47C20]" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full blur-3xl opacity-10 bg-[#E83E6F]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        {/* LEFT: Content */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="lg:col-span-6"
        >
         <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur">
           <Sparkles size={16} className="text-[#F47C20]" />
<span className="text-xs font-semibold tracking-[0.22em] uppercase text-white/75">
              Dietary Health Care Products
            </span>
          </div>

          <h2 className="mt-6 text-4xl md:text-5xl font-extrabold leading-tight text-white">
            Dietary Health{" "}
            <span className="bg-gradient-to-r from-[#F47C20] to-[#E83E6F] bg-clip-text text-transparent">
              Care Products
            </span>{" "}
            –
          </h2>

          <p className="mt-6 text-base md:text-lg leading-relaxed text-white/70">
            Amera Export stands as the leading manufacturer and exporter of dietary health care products,
            delivering exceptional dietary supplement manufacturing solutions globally. With a strong
            emphasis on research and development, we are committed to innovating advanced formulations
            that enhance health and well-being.
          </p>

          <p className="mt-4 text-base md:text-lg leading-relaxed text-white/70">
            Our state-of-the-art manufacturing facilities and strict quality control protocols ensure
            every product meets the highest global standards. Trusted by top brands in the USA and Europe,
            we continuously invest in cutting-edge technology and top talent to drive innovation and
            efficiency.
          </p>

          <p className="mt-4 text-base md:text-lg leading-relaxed text-white/70">
            Our comprehensive approach—from formulation and production to packaging and export—enables
            us to serve diverse markets with safe and effective products. Our commitment to continuous
            improvement, sustainable practices, and breakthrough research ensures our products remain at
            the forefront of innovation and efficacy.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <span className="rounded-full border border-black/10 bg-white px-4 py-2 text-sm text-[#2B2B2B]/70">
              USA & Europe Trusted
            </span>
            <span className="rounded-full border border-black/10 bg-white px-4 py-2 text-sm text-[#2B2B2B]/70">
              Export-ready Compliance
            </span>
            <span className="rounded-full border border-black/10 bg-white px-4 py-2 text-sm text-[#2B2B2B]/70">
              OEM / ODM Support
            </span>
          </div>

          <div className="mt-10 flex items-center gap-4">
            <motion.a
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              href="/products"
              className="inline-flex items-center justify-center rounded-md px-6 py-3 text-sm font-semibold text-white
                         bg-gradient-to-r from-[#F47C20] to-[#E83E6F] shadow-md shadow-black/10 hover:opacity-95 transition"
            >
              Explore Products
              <ArrowUpRight size={18} className="ml-2" />
            </motion.a>

            <motion.a
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              href="/contact"
              className="inline-flex items-center justify-center rounded-md px-6 py-3 text-sm font-semibold
                         border border-black/10 bg-white text-[#C62828] hover:text-[#F47C20] transition"
            >
              Talk to Experts
            </motion.a>
          </div>
        </motion.div>

        {/* RIGHT: Visual + Feature cards */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="lg:col-span-6"
        >
          <div className="relative">
            {/* Glass hero panel */}
            <motion.div
              variants={reveal}
             className="relative rounded-2xl border border-white/12 bg-white/6 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.35)] overflow-hidden"
            >
              {/* top gradient line */}
              <div className="h-[4px] w-full bg-gradient-to-r from-[#F47C20] via-[#C9A227] to-[#E83E6F]" />

              {/* subtle decorative blobs */}
              <div className="pointer-events-none absolute -top-16 -left-16 h-56 w-56 rounded-full bg-[#F47C20]/10 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-[#E83E6F]/10 blur-3xl" />

              <div className="p-6 md:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold tracking-[0.2em] uppercase text-white/60">
                      Manufacturing Excellence
                    </p>
                    <h3 className="mt-2 text-xl md:text-2xl font-bold text-white">
                      Research-led. Export-ready. Quality-assured.
                    </h3>
                    <p className="mt-3 text-sm md:text-base text-white/75 leading-relaxed">
                      Built for global brands seeking high-performance dietary supplement manufacturing with
                      consistent outcomes and reliable scale.
                    </p>
                  </div>

                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ repeat: Infinity, duration: 5.5 }}
                    className="hidden sm:flex h-12 w-12 rounded-xl bg-gradient-to-r from-[#F47C20] to-[#E83E6F] items-center justify-center text-white shadow-lg shadow-black/10"
                  >
                    <Globe2 size={22} />
                  </motion.div>
                </div>

                {/* mini metrics */}
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {[
                    { k: "100+", v: "Countries" },
                    { k: "OEM/ODM", v: "Support" },
                    { k: "GMP", v: "Process" },
                  ].map((m) => (
                    <div
                      key={m.v}
                      className="rounded-xl border border-black/10 bg-white px-4 py-3 text-center"
                    >
                      <div className="text-sm md:text-base font-bold text-[#C62828]">{m.k}</div>
                      <div className="text-[11px] md:text-xs text-[#2B2B2B]/65">{m.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Floating feature cards grid */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((f) => {
                const Icon = f.icon;
                return (
                  <motion.div
                    key={f.title}
                    variants={reveal}
                    whileHover={{ y: -6 }}
                    className="group rounded-2xl border border-white/12 bg-white/6 backdrop-blur-xl p-5 shadow-[0_16px_40px_rgba(0,0,0,0.28)] hover:shadow-[0_24px_60px_rgba(0,0,0,0.35)] transition"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded-xl bg-gradient-to-r from-[#F47C20] to-[#E83E6F] p-[1px]">
                        <div className="h-full w-full rounded-xl bg-white flex items-center justify-center">
                          <Icon size={20} className="text-[#C62828]" />
                        </div>
                      </div>

                      <div>
                        <p className="text-sm font-semibold text-white">{f.title}</p>
                        <p className="mt-1 text-sm text-white/70 leading-6">{f.desc}</p>
                      </div>
                    </div>

                    <div className="mt-4 h-[2px] w-16 bg-gradient-to-r from-[#F47C20] to-[#E83E6F] opacity-40 group-hover:opacity-100 transition" />
                  </motion.div>
                );
              })}
            </div>

            {/* extra floating decorative pill */}
            <motion.div
              animate={{ y: [0, -10, 0], rotate: [0, 2, 0] }}
              transition={{ repeat: Infinity, duration: 6 }}
              className="pointer-events-none hidden md:block absolute -right-8 top-12 rounded-full border border-black/10 bg-white px-4 py-2 shadow-md text-xs text-[#2B2B2B]/70"
            >
              Clinically-aligned formulation
            </motion.div>

            <motion.div
              animate={{ y: [0, 10, 0], rotate: [0, -2, 0] }}
              transition={{ repeat: Infinity, duration: 7 }}
              className="pointer-events-none hidden md:block absolute -left-10 bottom-10 rounded-full border border-black/10 bg-white px-4 py-2 shadow-md text-xs text-[#2B2B2B]/70"
            >
              Export documentation support
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}